/**
 * @file web/src/utils/seo/jsonLd.ts
 * @description schema.org structured-data builders (spec §16.4.2).
 *
 * Each builder takes a real API payload and returns a plain object
 * ready for JSON.stringify into a <script type="application/ld+json">.
 * Pages call these and pass the result to `<Seo jsonLd={...}>`.
 *
 * Never hand-type the JSON in a page component — these builders are
 * the single source of schema-version truth, so when schema.org
 * deprecates a field we update one file.
 */

import { SOCIAL_URLS } from '../../config/social';

const PUBLIC_ORIGIN =
  import.meta.env.VITE_PUBLIC_ORIGIN ||
  (typeof window !== 'undefined' ? window.location.origin : 'https://roundrecon.com');

const SITE_NAME = 'Round Recon';

/** WebSite + SearchAction — for the homepage (§16.4.2 #1). */
export function websiteWithSearch(): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: PUBLIC_ORIGIN,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${PUBLIC_ORIGIN}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

/** Organization metadata — paired with WebSite on the homepage. */
export function organization(): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: PUBLIC_ORIGIN,
    logo: `${PUBLIC_ORIGIN}/logo.png`,
    // Associate the brand with its public social profiles (§16.4.2).
    sameAs: [...SOCIAL_URLS],
  };
}

export interface BreadcrumbCrumb {
  name: string;
  url: string;
}

/** BreadcrumbList — every page deeper than home. */
export function breadcrumbList(crumbs: BreadcrumbCrumb[]): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: c.name,
      item: c.url.startsWith('http') ? c.url : `${PUBLIC_ORIGIN}${c.url}`,
    })),
  };
}

export interface FaqEntry {
  question: string;
  answer: string;
}

/**
 * FAQPage (#253). The `answer` text MUST match what's rendered on the page —
 * Google requires the Q&A be visible, not structured-data-only. Builders
 * upstream should feed the same plain-text answers shown to the user.
 */
export function faqPage(entries: FaqEntry[]): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: entries.map((e) => ({
      '@type': 'Question',
      name: e.question,
      acceptedAnswer: { '@type': 'Answer', text: e.answer },
    })),
  };
}

/**
 * Choose the breadcrumb trail for a search-results page.
 *
 * When the search narrows to exactly one caliber facet, emit a
 * caliber-specific trail (`Round Recon › Ammo › 9mm Luger`) so the
 * high-traffic facet pages carry meaningful structured data. The leaf
 * label is the caliber value verbatim — that value is already the
 * canonical taxonomy name the URL/sitemap use, so it must not be
 * re-cased or normalized (".223 Remington" must stay exact).
 *
 * Any other shape (zero, blank, or multiple calibers) degrades to the
 * generic `Round Recon › Search` trail rather than risk a misleading
 * single-caliber crumb.
 *
 * @param calibers selected caliber values from the parsed search query
 * @param canonical the page's canonical URL (the breadcrumb leaf)
 * @returns ordered crumbs for {@link breadcrumbList}
 */
export function searchBreadcrumbCrumbs(calibers: string[], canonical: string): BreadcrumbCrumb[] {
  const single =
    calibers.length === 1 && calibers[0]!.trim().length > 0 ? calibers[0]!.trim() : null;
  if (single) {
    return [
      { name: SITE_NAME, url: '/' },
      { name: 'Ammo', url: '/search' },
      { name: single, url: canonical },
    ];
  }
  return [
    { name: SITE_NAME, url: '/' },
    { name: 'Search', url: canonical },
  ];
}

export interface OfferInput {
  price: number | null;
  priceCurrency?: string;
  availability?: 'in_stock' | 'limited' | 'out_of_stock' | 'unknown';
  sellerName: string;
  url: string;
  priceValidUntil?: string;
  /**
   * Effective price-per-round (CPR) for this listing. When present and
   * positive, it is emitted as a schema.org UnitPriceSpecification so the
   * per-round price — the metric that actually drives this vertical — is
   * machine-readable to search engines and LLM answer engines.
   */
  pricePerRound?: number | null;
}

/**
 * Build a schema.org UnitPriceSpecification expressing price-per-round,
 * or `undefined` when CPR is missing/non-positive.
 *
 * @param pricePerRound effective $/round
 * @param currency ISO currency for the offer
 */
function unitPriceSpecification(
  pricePerRound: number | null | undefined,
  currency: string
): object | undefined {
  if (pricePerRound == null || !Number.isFinite(pricePerRound) || pricePerRound <= 0) {
    return undefined;
  }
  return {
    '@type': 'UnitPriceSpecification',
    // Round to 4 dp to preserve sub-cent CPR precision (e.g. $0.2134/round).
    price: Number(pricePerRound.toFixed(4)),
    priceCurrency: currency,
    unitText: 'round',
    referenceQuantity: { '@type': 'QuantitativeValue', value: 1, unitText: 'round' },
  };
}

export interface ProductInput {
  name: string;
  description?: string;
  image?: string;
  sku?: string;
  brand?: string;
  category?: string;
  url: string;
  offers: OfferInput[];
}

/**
 * Product + offers (§16.4.2 #2). When `offers.length > 1`, wraps them
 * in an `AggregateOffer`; otherwise emits a single `Offer`.
 */
export function product(p: ProductInput): object {
  const offerObjects = p.offers
    .filter((o) => o.price != null && o.price > 0)
    .map((o) => {
      const currency = o.priceCurrency ?? 'USD';
      const cprSpec = unitPriceSpecification(o.pricePerRound, currency);
      return {
        '@type': 'Offer',
        price: o.price,
        priceCurrency: currency,
        availability: mapAvailability(o.availability),
        seller: { '@type': 'Organization', name: o.sellerName },
        url: o.url,
        ...(o.priceValidUntil ? { priceValidUntil: o.priceValidUntil } : {}),
        ...(cprSpec ? { priceSpecification: cprSpec } : {}),
      };
    });

  const offersField =
    offerObjects.length > 1
      ? {
          '@type': 'AggregateOffer',
          offerCount: offerObjects.length,
          lowPrice: Math.min(...offerObjects.map((o) => Number(o.price))),
          highPrice: Math.max(...offerObjects.map((o) => Number(o.price))),
          priceCurrency: 'USD',
          offers: offerObjects,
        }
      : offerObjects[0];

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: p.name,
    ...(p.description ? { description: p.description } : {}),
    ...(p.image ? { image: p.image } : {}),
    ...(p.sku ? { sku: p.sku } : {}),
    ...(p.brand ? { brand: { '@type': 'Brand', name: p.brand } } : {}),
    ...(p.category ? { category: p.category } : {}),
    // Ammunition is sold new — declaring condition helps Google's
    // product/merchant rich results and avoids a "missing field" warning.
    itemCondition: 'https://schema.org/NewCondition',
    url: p.url,
    ...(offersField ? { offers: offersField } : {}),
  };
}

/** Retailer Organization metadata for retailer-detail pages. */
export function retailerOrg(input: {
  name: string;
  slug: string;
  url: string;
  logo?: string;
  description?: string;
}): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: input.name,
    url: input.url,
    identifier: input.slug,
    ...(input.logo ? { logo: input.logo } : {}),
    ...(input.description ? { description: input.description } : {}),
  };
}

export interface ItemListEntry {
  position: number;
  url: string;
  name?: string;
}

/** ItemList for search-results / retailer-product-list pages. */
export function itemList(items: ItemListEntry[]): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: items.map((it) => ({
      '@type': 'ListItem',
      position: it.position,
      ...(it.name ? { name: it.name } : {}),
      url: it.url.startsWith('http') ? it.url : `${PUBLIC_ORIGIN}${it.url}`,
    })),
  };
}

/**
 * Map our internal stockStatus enum to schema.org availability URIs.
 *
 * @param s our stockStatus
 * @returns schema.org availability URL
 */
function mapAvailability(s?: string): string {
  switch (s) {
    case 'in_stock':
      return 'https://schema.org/InStock';
    case 'limited':
      return 'https://schema.org/LimitedAvailability';
    case 'out_of_stock':
      return 'https://schema.org/OutOfStock';
    default:
      return 'https://schema.org/InStock';
  }
}
